/**
 * The correction for making many comparisons at once (M4S-5777, X74). Spec:
 * docs/pdlc/crosstabs-significance-full.md §4.
 *
 * Off by default (PRD decision 11). When on, it is applied to one FAMILY of tests at a time: the
 * pairwise tests of one row within one banner group (decision S3). `sig-pairs.ts` says which tests
 * make a family; this module only adjusts a family's p-values. Pure, and nothing else in the
 * crosstab adjusts a p-value.
 */

export type SigCorrection = 'none' | 'bonferroni' | 'bh';

export const SIG_CORRECTIONS: readonly SigCorrection[] = ['none', 'bonferroni', 'bh'];

/** What the rail, the legend and every file call each method. */
export const SIG_CORRECTION_LABELS: Record<SigCorrection, string> = {
    none: 'Off',
    bonferroni: 'Bonferroni',
    bh: 'Benjamini–Hochberg (false discovery rate)',
};

/** A stored value as a method; anything unknown (an old slot, a DANI blob) is off. */
export function toSigCorrection(v: unknown): SigCorrection {
    return v === 'bonferroni' || v === 'bh' ? v : 'none';
}

/**
 * One family's p-values adjusted for the number of tests in it, in the order given.
 *
 * - `bonferroni`: p · m, capped at 1.
 * - `bh`: Benjamini–Hochberg step-up, p₍ᵢ₎ · m / i made monotone from the largest down, capped at 1.
 *   An adjusted p-value under α is the BH procedure at false discovery rate α.
 * - `none`: the p-values as they are (a copy).
 */
export function adjustPValues(ps: readonly number[], method: SigCorrection): number[] {
    const m = ps.length;
    if (method === 'none' || m <= 1) return [...ps];
    if (method === 'bonferroni') return ps.map((p) => Math.min(1, p * m));
    const order = ps.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
    const out = new Array<number>(m);
    let running = 1;
    for (let k = m - 1; k >= 0; k--) {
        const { p, i } = order[k];
        running = Math.min(running, (p * m) / (k + 1));
        out[i] = Math.min(1, running);
    }
    return out;
}
